import { forwardRef, type WheelEventHandler } from 'react';

import type { TimeFormat } from '@app/api/Branches/types';

import type {
  AppointmentPreviewField,
  CalendarBreak,
  CalendarEvent,
  CalendarSlotMenu,
  Employee,
} from '@pages/Calendar/data';

import { DragPreviewOverlay } from '../../DragPreviewOverlay';
import { EmployeeColumn } from '../../EmployeeColumn';

import type { EventDragState } from '../../AppointmentCard/hooks';

import '../styles/EmployeesViewport.scss';

interface EmployeesViewportProps {
  employees: Employee[];
  events: CalendarEvent[];
  startTime: string;
  endTime: string;
  bookingStepMinutes: number;
  timeFormat?: TimeFormat | null;
  intervalHeightPx: number;
  columnWidth: number;
  totalTrackWidth: number;
  hasOverflow: boolean;
  trackTransform?: string;
  onWheel: WheelEventHandler<HTMLDivElement>;
  onSlotClick: (employeeId: string, time: string, endTime: string) => void;
  onSlotBreakClick?: (
    employeeId: string,
    time: string,
    endTime: string
  ) => void;
  onBreakClick: (employeeId: string, calendarBreak: CalendarBreak) => void;
  onEventUpdate: (
    eventId: string,
    newStart: string,
    newEnd: string,
    newEmployeeId?: string
  ) => void;
  onRecordClick: (event: CalendarEvent) => void;
  dragState: EventDragState | null;
  dragStateChangeHandler: (state: EventDragState | null) => void;
  canShowContacts: boolean;
  previewFields: AppointmentPreviewField[];
  activeSlotMenu: CalendarSlotMenu | null;
  onSlotMenuOpen: (slotMenu: CalendarSlotMenu) => void;
  onSlotMenuClose: () => void;
}

export const EmployeesViewport = forwardRef<
  HTMLDivElement,
  EmployeesViewportProps
>(
  (
    {
      employees,
      events,
      startTime,
      endTime,
      bookingStepMinutes,
      timeFormat,
      intervalHeightPx,
      columnWidth,
      totalTrackWidth,
      hasOverflow,
      trackTransform,
      onWheel,
      onSlotClick,
      onSlotBreakClick,
      onBreakClick,
      onEventUpdate,
      onRecordClick,
      dragState,
      dragStateChangeHandler,
      canShowContacts,
      previewFields,
      activeSlotMenu,
      onSlotMenuOpen,
      onSlotMenuClose,
    },
    ref
  ) => (
    <div
      className="EmployeesViewport"
      ref={ref}
      data-testid="employees-viewport"
      onWheel={onWheel}
    >
      <div
        className="EmployeesViewport__track"
        style={{
          width: hasOverflow && totalTrackWidth ? totalTrackWidth : '100%',
          transform: trackTransform,
        }}
      >
        {employees.map((employee) => {
          const employeeEvents = events.filter(
            ({ employeeId }) => employeeId === employee.id
          );

          return (
            <div
              key={employee.id}
              className="EmployeesViewport__column"
              data-employee-column-id={employee.id}
              style={{
                width: hasOverflow && columnWidth ? columnWidth : undefined,
                flex:
                  hasOverflow && columnWidth
                    ? `0 0 ${columnWidth}px`
                    : '1 1 0%',
              }}
            >
              <EmployeeColumn
                employee={employee}
                employees={employees}
                events={employeeEvents}
                startTime={startTime}
                endTime={endTime}
                stepMinutes={bookingStepMinutes}
                timeFormat={timeFormat}
                intervalHeightPx={intervalHeightPx}
                showHeader={false}
                onSlotClick={onSlotClick}
                onSlotBreakClick={onSlotBreakClick}
                onBreakClick={onBreakClick}
                onEventUpdate={onEventUpdate}
                onDragStateChange={dragStateChangeHandler}
                onRecordClick={onRecordClick}
                canShowContacts={canShowContacts}
                previewFields={previewFields}
                activeSlotMenu={activeSlotMenu}
                onSlotMenuOpen={onSlotMenuOpen}
                onSlotMenuClose={onSlotMenuClose}
              />
            </div>
          );
        })}

        {dragState !== null &&
          dragState.targetEmployeeId !== dragState.sourceEmployeeId && (
            <DragPreviewOverlay
              dragState={dragState}
              employees={employees}
              columnWidth={columnWidth}
              hasOverflow={hasOverflow}
            />
          )}
      </div>
    </div>
  )
);

EmployeesViewport.displayName = 'EmployeesViewport';
